import React from 'react'
import { useDialog } from '../hooks/useDialog'
import { IconButton } from '../ui/Icon'
import { CANCEL_BOTH, CANCEL_PRO_ASK, CANCEL_PRO_LINE, CANCEL_PRO_LOCKS, KEEP_CARD_PAYMENTS, KEEP_CARD_PAYMENTS_NOTE, VAT_LINE, cancelBothNote, keepLabel } from './rungs'
import '../components/Sheet.css'
import './billing.css'

interface Props {
  // endsOn is the period's end, "13 November", for the cancel-both note.
  endsOn: string | null
  // overdue: her last payment failed. Switching to Payments would charge that
  // card at once, so only ending both is offered, with the payment still due.
  overdue?: boolean
  busy: boolean
  error: string
  onKeepPayments: () => void
  onCancelBoth: () => void
  onClose: () => void
}

// CancelProSheet is the question Cancel Pro opens (owner, 2026-10-25), as a
// sheet over the plan card rather than a stretch of it: what she loses, then
// switching to Payments (card payments on their own) or ending both at the
// period end. It is the app's danger pattern (docs/25, review 2026-10-25):
// one solid red, each choice with its note, and the way back, Keep Pro, as
// a full-width secondary button. It renders in place, like Settings' other
// sheets: the Settings page sits above a sheet portaled to the body.
export default function CancelProSheet({ endsOn, overdue = false, busy, error, onKeepPayments, onCancelBoth, onClose }: Props) {
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })
  const [chose, setChose] = React.useState<'switch' | 'cancel' | null>(null)
  return (
    <div className="sheet-overlay" onClick={() => { if (!busy) onClose() }}>
      <div
        ref={dialogRef}
        className="sheet-content sheet-content--closable rung-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={CANCEL_PRO_ASK}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle"></div>
        <IconButton icon="close" label="Close" className="sheet-close" onClick={onClose} disabled={busy} />
        <div className="sheet-header sheet-header--closable">
          <h2>{CANCEL_PRO_ASK}</h2>
          <p>{overdue ? CANCEL_PRO_LOCKS : CANCEL_PRO_LINE}</p>
        </div>
        <div className="sheet-body">
          {error && <p className="field-error" role="alert">{error}</p>}
          <div className="sheet-stack">
            {!overdue && (
              <>
                <button type="button" className="btn btn-secondary btn-block" onClick={() => { setChose('switch'); onKeepPayments() }} disabled={busy}>
                  {busy && chose === 'switch' ? 'Switching…' : KEEP_CARD_PAYMENTS}
                </button>
                <p className="field-hint billing-note">{KEEP_CARD_PAYMENTS_NOTE}</p>
              </>
            )}
            <button type="button" className="btn btn-danger-solid btn-block" onClick={() => { setChose('cancel'); onCancelBoth() }} disabled={busy}>
              {busy && chose === 'cancel' ? 'Cancelling…' : CANCEL_BOTH}
            </button>
            <p className="field-hint billing-note">{cancelBothNote(endsOn, overdue)}</p>
            <button type="button" className="btn btn-secondary btn-block" onClick={onClose} disabled={busy}>
              {keepLabel('pro')}
            </button>
          </div>
          {/* The VAT line goes with the £14 it qualifies. */}
          {!overdue && <p className="field-hint billing-note">{VAT_LINE}</p>}
        </div>
      </div>
    </div>
  )
}
